import { X } from "lucide-react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type ToastKind = "info" | "success" | "error";

interface ToastNotice {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  notify: (message: string, kind?: ToastKind) => void;
}

const listeners = new Set<(notice: ToastNotice) => void>();
let nextNoticeId = 0;

export const notifyAction = (message: string, kind: ToastKind = "info") => {
  const notice = { id: ++nextNoticeId, kind, message };
  for (const listener of listeners) listener(notice);
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [notice, setNotice] = useState<ToastNotice | null>(null);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => setNotice(null), []);

  useEffect(() => {
    const receive = (nextNotice: ToastNotice) => {
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
      setNotice(nextNotice);
      dismissTimer.current = setTimeout(() => setNotice(null), 5_000);
    };
    listeners.add(receive);
    return () => {
      listeners.delete(receive);
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      notify: notifyAction,
    }),
    [],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="cyra-toast-region" aria-live="polite" aria-atomic="true">
        {notice ? (
          <div
            key={notice.id}
            className="cyra-toast"
            data-kind={notice.kind}
            role={notice.kind === "error" ? "alert" : "status"}
          >
            <span>{notice.message}</span>
            <button
              type="button"
              className="cyra-toast-close"
              onClick={dismiss}
              aria-label="Dismiss"
            >
              <X aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider.");
  return context;
}
